import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ChatContext = createContext(null);

// API Configuration
// Local: http://localhost:8000 | Production: https://api.cloudmortgage.io
const API_URL = 'https://api.cloudmortgage.io';
const CHAT_API = `${API_URL}/api/borrower-chat/ask/stream`;

// Vietnamese Mortgage Knowledge Base for fallback responses
const knowledgeBase = [
  {
    keywords: ['quy trình', 'bắt đầu', 'mua nhà', 'các bước', 'làm sao'],
    response: `Quy trình vay mua nhà gồm 5 bước chính:

1. **Kiểm tra tài chính**: Xem điểm tín dụng (cần từ 580+), tính DTI (nên dưới 43%), chuẩn bị tiền Down Payment

2. **Xin Pre-Approval**: Nộp giấy tờ (W-2, thuế, sao kê ngân hàng) để được thư Pre-Approval trong 24-48 giờ

3. **Tìm nhà**: Làm việc với môi giới, xem ít nhất 5-10 nhà

4. **Đặt giá và thương lượng**: Viết đề nghị mua với điều kiện

5. **Closing**: Hoàn tất khoản vay và nhận chìa khóa!

Bạn cần giúp đỡ ở bước nào?`
  },
  {
    keywords: ['điểm tín dụng', 'credit score', 'tín dụng', 'fico'],
    response: `Yêu cầu điểm tín dụng tùy thuộc vào loại vay:

• **FHA Loan**: 580+ (với 3.5% down) hoặc 500-579 (với 10% down)
• **Conventional**: 620+ (lý tưởng là 740+)
• **VA Loan**: Không có yêu cầu tối thiểu chính thức, thường từ 620+
• **USDA**: Thường từ 640+
• **Jumbo Loan**: 700+

**Cách cải thiện điểm tín dụng:**
- Thanh toán hóa đơn đúng hạn
- Giữ tỷ lệ sử dụng thẻ tín dụng dưới 30%
- Không mở nhiều tài khoản mới
- Kiểm tra và sửa lỗi trên báo cáo tín dụng

Bạn muốn biết thêm về loại vay nào?`
  },
  {
    keywords: ['down payment', 'đặt cọc', 'tiền trước', 'tiền đặt'],
    response: `Yêu cầu Down Payment theo loại vay:

• **Conventional**: 3-20% (PMI nếu dưới 20%)
• **FHA**: 3.5% (với điểm 580+) hoặc 10% (điểm 500-579)
• **VA**: 0% - Không cần down payment!
• **USDA**: 0% - Cho khu vực nông thôn

**Chương trình hỗ trợ:**
- First-time buyer programs
- Down payment assistance grants
- State/local government programs
- Employer assistance programs

**Ví dụ:**
Nhà $400,000 × 3.5% FHA = $14,000 down payment

Bạn cần thông tin về chương trình hỗ trợ cụ thể nào không?`
  },
  {
    keywords: ['fha', 'conventional', 'so sánh', 'loại vay', 'khác nhau'],
    response: `So sánh FHA vs Conventional:

**FHA Loan:**
✅ Điểm tín dụng thấp hơn (580+)
✅ Down payment thấp (3.5%)
✅ DTI có thể lên đến 50%
❌ Bắt buộc có PMI suốt thời gian vay
❌ Giới hạn số tiền vay theo vùng

**Conventional Loan:**
✅ Không cần PMI nếu down 20%+
✅ Lãi suất thấp hơn cho điểm cao
✅ Không giới hạn số tiền vay
❌ Yêu cầu điểm tín dụng cao hơn (620+)
❌ DTI thường dưới 43%

**Nên chọn:**
- FHA: Điểm tín dụng thấp, tiết kiệm chưa nhiều
- Conventional: Điểm cao, có tiền down 20%+

Bạn có câu hỏi cụ thể về loại nào không?`
  },
  {
    keywords: ['va', 'quân đội', 'cựu chiến binh', 'veteran'],
    response: `VA Loan - Dành cho Cựu Chiến Binh:

**Ưu điểm:**
✅ Không cần Down Payment (0%)
✅ Không cần PMI
✅ Lãi suất thường thấp hơn
✅ Không giới hạn số tiền vay
✅ Có thể sử dụng nhiều lần

**Điều kiện:**
- 90 ngày phục vụ thời chiến
- 181 ngày phục vụ thời bình
- 6 năm trong National Guard/Reserves
- Vợ/chồng của quân nhân tử nạn

**Cần chuẩn bị:**
- Certificate of Eligibility (COE)
- DD-214 (cho cựu quân nhân)

VA loan là một trong những lợi ích tốt nhất cho cựu chiến binh!`
  },
  {
    keywords: ['dti', 'nợ', 'thu nhập', 'tỷ lệ'],
    response: `DTI (Debt-to-Income) - Tỷ lệ Nợ/Thu Nhập:

**Công thức:**
DTI = (Tổng nợ hàng tháng / Thu nhập gộp) × 100

**Yêu cầu theo loại vay:**
• Conventional: ≤43% (lý tưởng ≤36%)
• FHA: ≤50%
• VA: Không có giới hạn cứng, xét từng trường hợp

**Nợ bao gồm:**
- Khoản trả nhà mới (PITI)
- Thẻ tín dụng (minimum payment)
- Vay ô tô
- Student loans
- Child support/alimony

**Ví dụ:**
Thu nhập: $8,000/tháng
Tổng nợ: $2,800/tháng
DTI = 2,800/8,000 = 35% ✅

Bạn muốn tôi giúp tính DTI không?`
  },
  {
    keywords: ['pmi', 'bảo hiểm', 'mortgage insurance'],
    response: `PMI (Private Mortgage Insurance):

**PMI là gì?**
Bảo hiểm bảo vệ người cho vay khi bạn đặt cọc dưới 20%

**Chi phí:**
0.5% - 1% giá trị vay/năm
Ví dụ: Vay $300,000 = $1,500-$3,000/năm ($125-250/tháng)

**Cách tránh/bỏ PMI:**
✅ Down payment 20%+
✅ Yêu cầu bỏ PMI khi equity đạt 20%
✅ Tự động bỏ khi equity đạt 22%
✅ Refinance khi giá nhà tăng

**Lưu ý:**
- FHA: MIP bắt buộc suốt thời gian vay
- VA: Không có PMI (có Funding Fee 1 lần)

PMI không xấu - nó giúp bạn mua nhà sớm hơn!`
  },
  {
    keywords: ['closing', 'chi phí đóng', 'phí', 'closing cost'],
    response: `Closing Costs - Chi Phí Đóng:

**Ước tính:** 2-5% giá mua nhà

**Bao gồm:**
📋 **Phí cho vay:**
- Origination fee (0.5-1%)
- Appraisal ($300-600)
- Credit report ($30-50)

📋 **Phí Title:**
- Title search & insurance
- Attorney fees
- Recording fees

📋 **Prepaid items:**
- Property tax (2-3 tháng)
- Homeowner insurance (1 năm)
- Interest prepaid

**Ví dụ nhà $400,000:**
Closing costs: $8,000 - $20,000

**Mẹo tiết kiệm:**
- Yêu cầu seller credit
- So sánh nhiều lender
- Hỏi về lender credits

Bạn cần biết thêm chi tiết nào?`
  },
  {
    keywords: ['giấy tờ', 'tài liệu', 'cần gì', 'chuẩn bị'],
    response: `Giấy tờ cần chuẩn bị khi xin vay:

📁 **Thu nhập:**
- W-2 (2 năm gần nhất)
- Pay stubs (30 ngày gần nhất)
- Tax returns (2 năm)
- Self-employed: Schedule C/1099s

📁 **Tài sản:**
- Bank statements (2 tháng)
- Investment accounts
- Retirement accounts
- Gift letter (nếu có)

📁 **Nhận dạng:**
- ID/Driver's license
- Social Security card
- Green card/Work permit

📁 **Nợ:**
- Mortgage statements hiện tại
- Auto/student loan statements
- Credit card statements

📁 **Bất động sản:**
- Purchase agreement
- HOA docs
- Homeowner insurance quote

Chuẩn bị đầy đủ giúp đẩy nhanh quá trình!`
  },
  {
    keywords: ['pre-approval', 'pre approval', 'preapproval', 'thư chấp thuận'],
    response: `Pre-Approval - Thư Chấp Thuận Trước:

**Pre-Approval là gì?**
Xác nhận từ lender về số tiền bạn có thể vay

**Tại sao quan trọng?**
✅ Biết ngân sách thực tế
✅ Seller tin tưởng hơn
✅ Đề nghị mua mạnh hơn
✅ Đóng nhanh hơn

**Quy trình:**
1. Nộp đơn online/gặp loan officer
2. Cung cấp giấy tờ
3. Lender kiểm tra tín dụng
4. Nhận thư trong 24-48 giờ

**Hiệu lực:** 60-90 ngày

**Khác với Pre-Qualification:**
- Pre-Qual: Ước tính nhanh, không xác minh
- Pre-Approval: Xác minh kỹ, có giá trị hơn

Bạn sẵn sàng xin Pre-Approval chưa?`
  }
];

// Find best matching response from knowledge base
const findKnowledgeBaseResponse = (message) => {
  const lowerMessage = message.toLowerCase();

  for (const item of knowledgeBase) {
    const matchCount = item.keywords.filter(keyword =>
      lowerMessage.includes(keyword.toLowerCase())
    ).length;

    if (matchCount > 0) {
      return item.response;
    }
  }

  // Default response if no match
  return `Cảm ơn câu hỏi của bạn! Tôi có thể giúp bạn tìm hiểu về:

• Quy trình vay mua nhà
• Các loại vay (FHA, Conventional, VA, USDA)
• Yêu cầu điểm tín dụng
• Down payment và closing costs
• DTI và PMI
• Giấy tờ cần chuẩn bị

Bạn muốn tìm hiểu về chủ đề nào?`;
};

// Generate a unique session ID for this chat session (format: nhamy-{timestamp}-{random})
const generateSessionId = () => {
  return `nhamy-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingStep, setThinkingStep] = useState(null);
  const streamingMessageRef = useRef('');
  const [sessionId, setSessionId] = useState(() => {
    // Try to get existing session from localStorage, or create new one
    const existingSession = localStorage.getItem('nhamy_chat_session');
    if (existingSession) {
      return existingSession;
    }
    const newSession = generateSessionId();
    localStorage.setItem('nhamy_chat_session', newSession);
    return newSession;
  });

  // Initialize with welcome message
  const initializeChat = useCallback(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          type: 'bot',
          content: 'Xin chào! Tôi là Trợ Lý Vay Nhà của Nhamy.us. Tôi có thể giúp bạn tìm hiểu về quy trình vay mua nhà tại Mỹ. Bạn có câu hỏi gì không?',
          timestamp: new Date().toISOString()
        }
      ]);
    }
  }, [messages.length]);

  // Toggle chat open/close
  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) {
        // Opening chat - initialize if needed
        initializeChat();
      }
      return !prev;
    });
  }, [initializeChat]);

  // Send message to chatbot API with SSE streaming
  const sendMessage = useCallback(async (content) => {
    if (!content.trim() || isLoading || isStreaming) return;

    // Add user message to chat
    const userMessage = {
      id: 'user_' + Date.now(),
      type: 'user',
      content: content.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsStreaming(false);
    streamingMessageRef.current = '';

    // Create placeholder for bot response
    const botMessageId = 'bot_' + Date.now();

    try {
      const response = await fetch(CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: content.trim(),
          session_id: sessionId,
          language: 'vi',
          source: 'nhamy'
        })
      });

      if (!response.ok) {
        throw new Error('API unavailable');
      }

      // Handle SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      setIsLoading(false);
      setIsStreaming(true);

      // Add empty bot message that will be updated with streaming content
      setMessages(prev => [...prev, {
        id: botMessageId,
        type: 'bot',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'chunk') {
                // Append content to streaming message
                streamingMessageRef.current += data.content;
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, content: streamingMessageRef.current }
                    : msg
                ));
              } else if (data.type === 'thinking_step') {
                // Show thinking indicator
                setThinkingStep({ title: data.title, status: data.status });
              } else if (data.type === 'done') {
                // Conversation complete - finalize message
                setMessages(prev => prev.map(msg =>
                  msg.id === botMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                ));
                setThinkingStep(null);
              }
            } catch (parseError) {
              // Skip invalid JSON lines
              console.log('Skipping non-JSON SSE line');
            }
          }
        }
      }

      setIsStreaming(false);
      setThinkingStep(null);

    } catch (error) {
      console.log('Using local knowledge base fallback:', error.message);

      setIsLoading(false);
      setIsStreaming(false);

      // Use local knowledge base as fallback
      // Add small delay to simulate response time
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 500));

      const fallbackResponse = findKnowledgeBaseResponse(content.trim());

      setMessages(prev => [...prev, {
        id: botMessageId,
        type: 'bot',
        content: fallbackResponse,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isLoading, isStreaming, sessionId]);

  // Clear chat history and start new session
  const clearChat = useCallback(() => {
    const newSession = generateSessionId();
    localStorage.setItem('nhamy_chat_session', newSession);
    setSessionId(newSession);
    streamingMessageRef.current = '';
    setThinkingStep(null);
    setMessages([
      {
        id: 'welcome',
        type: 'bot',
        content: 'Xin chào! Tôi là Trợ Lý Vay Nhà của Nhamy.us. Tôi có thể giúp bạn tìm hiểu về quy trình vay mua nhà tại Mỹ. Bạn có câu hỏi gì không?',
        timestamp: new Date().toISOString()
      }
    ]);
  }, []);

  return (
    <ChatContext.Provider value={{
      messages,
      isOpen,
      isLoading,
      isStreaming,
      thinkingStep,
      sessionId,
      toggleChat,
      sendMessage,
      clearChat,
      initializeChat
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
